import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Volume2, FileDown, Send, CheckCircle2 } from "lucide-react";
import { StickyBack } from "../../components/StickyBack";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

function Block({ block, answers, setAnswer }) {
  switch (block.type) {
    case "consigne":
      return (
        <div className="rounded-2xl border-l-4 border-bordeaux bg-bordeaux-tint/50 p-5">
          <p className="eyebrow text-bordeaux">Consignes</p>
          <h3 className="mt-1 font-sans font-medium text-xl text-ink">{block.title}</h3>
          <p className="mt-2 text-ink-soft">{block.text}</p>
        </div>
      );
    case "video":
      return (
        <div>
          <h3 className="mb-3 font-sans font-medium text-xl text-ink">{block.title}</h3>
          <div className="aspect-video overflow-hidden rounded-2xl border border-line bg-black">
            <iframe src={block.url} title={block.title} className="h-full w-full" allowFullScreen frameBorder="0" />
          </div>
        </div>
      );
    case "audio":
      return (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-white p-5">
          <Volume2 size={20} className="text-bordeaux" />
          <div className="flex-1">
            <p className="text-sm font-medium text-ink">{block.title}</p>
            <audio controls src={block.url} className="mt-2 w-full" />
          </div>
        </div>
      );
    case "texte":
      return (
        <div className="rounded-2xl border border-line bg-white p-5">
          <h3 className="font-sans font-medium text-xl text-ink">{block.title}</h3>
          <p className="mt-2 text-ink-soft">{block.text}</p>
        </div>
      );
    case "exercice":
      return (
        <div className="rounded-2xl border border-line bg-white p-6">
          <p className="eyebrow">Exercice</p>
          <h3 className="mt-1 font-sans font-medium text-xl text-ink">{block.title}</h3>
          <div className="mt-4 space-y-5">
            {block.questions.map((q, i) => (
              <div key={i}>
                <label className="text-sm font-medium text-ink">{i + 1}. {q}</label>
                <textarea
                  data-testid={`exercise-answer-${i}`}
                  rows={3}
                  value={answers[i] || ""}
                  onChange={(e) => setAnswer(i, e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-line bg-cream/40 p-3 outline-none focus:border-bordeaux focus:bg-white"
                  placeholder="Ta réponse…"
                />
              </div>
            ))}
          </div>
        </div>
      );
    case "ressource":
      return (
        <div className="rounded-2xl border border-line bg-white p-5">
          <h3 className="font-sans font-medium text-xl text-ink">{block.title}</h3>
          <div className="mt-3 space-y-2">
            {block.items.map((r, i) => (
              <a key={i} href={r.url} className="flex items-center gap-2 rounded-lg border border-line px-4 py-2.5 text-sm text-bordeaux transition hover:bg-bordeaux-tint">
                <FileDown size={16} /> {r.label}
              </a>
            ))}
          </div>
        </div>
      );
    case "travail":
      return (
        <div className="rounded-2xl border border-dashed border-bordeaux-border bg-bordeaux-tint/40 p-5">
          <h3 className="font-sans font-medium text-xl text-bordeaux">{block.title}</h3>
          <p className="mt-1 text-sm text-ink-soft">{block.text}</p>
        </div>
      );
    default:
      return null;
  }
}

export default function ModuleView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");

  // auto-save answers locally
  useEffect(() => {
    api.get(`/client/modules/${id}`).then(({ data }) => setModule(data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
    const saved = localStorage.getItem(`answers-${id}`);
    if (saved) setAnswers(JSON.parse(saved));
  }, [id]);

  const setAnswer = (i, v) => {
    const next = { ...answers, [i]: v };
    setAnswers(next);
    localStorage.setItem(`answers-${id}`, JSON.stringify(next));
  };

  const submit = async () => {
    await api.post(`/client/modules/${id}/complete`);
    toast.success("Envoyé à Fanny ! Ton travail est en attente de correction.");
    navigate("/espace/parcours");
  };

  if (error) return (
    <div className="mx-auto max-w-lg rounded-2xl border border-line bg-white p-8 text-center">
      <p className="text-bordeaux">{error}</p>
      <button onClick={() => navigate("/espace/parcours")} className="mt-4 rounded-full bg-bordeaux px-5 py-2.5 text-sm text-white">Retour au parcours</button>
    </div>
  );
  if (!module) return <div className="text-ink-muted">Chargement…</div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <StickyBack onClick={() => navigate("/espace/parcours")}>Retour au parcours</StickyBack>
      <div>
        <p className="eyebrow">Semaine {module.week} · Phase {module.phase}</p>
        <h1 className="mt-2 font-sans font-medium text-4xl text-ink">{module.title}</h1>
      </div>

      {module.content_blocks?.map((b, i) => (
        <Block key={i} block={b} answers={answers} setAnswer={setAnswer} />
      ))}

      <div className="sticky bottom-4 flex items-center justify-between rounded-2xl border border-line bg-white p-4 shadow-editorial">
        <span className="text-xs text-ink-muted">💾 Tes réponses sont sauvegardées automatiquement</span>
        {module.completed_by_client ? (
          <span className="flex items-center gap-2 rounded-full bg-[#FEF3C7] px-4 py-2 text-sm text-[#92400E]">
            <CheckCircle2 size={16} /> En attente de correction
          </span>
        ) : (
          <button data-testid="submit-exercise-button" onClick={submit}
            className="flex items-center gap-2 rounded-full bg-bordeaux px-6 py-3 text-sm font-medium text-white transition hover:bg-bordeaux-dark">
            <Send size={16} /> Envoyer mon exercice à Fanny
          </button>
        )}
      </div>
    </div>
  );
}
